import { useParams } from 'react-router-dom';

import { Loader } from 'components/Loader';
import { useGetDetailedTotemsOfOrganizationQuery } from 'data/__generated__';
import { dateFromString } from 'helpers/dateTimes';

import { RecentOrders, type RecentOrderRow } from 'pages/Orders/components/RecentOrders';

type ParamTypes = {
    organizationId: string;
};

export const OrdersDetails = () => {
    const { organizationId = '' } = useParams<ParamTypes>();

    // Reuses the same query as the Totems section — Apollo dedupes it from cache, so no extra network round-trip.
    // An organization has no "recent orders" relation of its own: orders hang off totems (the API returns the 3
    // last deliveries per totem), so the organization view is the union of its totems' recent orders.
    const { loading, error, data } = useGetDetailedTotemsOfOrganizationQuery({
        variables: { organizationId },
    });

    if (loading) {
        return (
            <div className="flex min-h-[200px] w-full items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (error || !data) {
        throw new Error('Une erreur est survenue lors de la récupération des commandes');
    }

    const orders: RecentOrderRow[] = data.totemsOfOrganization
        .flatMap((totem) =>
            totem.recentOrders.map((order) => ({
                ...order,
                totemId: totem._id,
                totemFullname: totem.fullname,
            })),
        )
        // `deliveryDate` is a DD/MM/YYYY string: sort on the parsed date so the whole organization reads
        // most-recent-first, all totems mixed together.
        .sort(
            (orderA, orderB) =>
                dateFromString(orderB.deliveryDate).getTime() - dateFromString(orderA.deliveryDate).getTime(),
        );

    return <RecentOrders orders={orders} organizationId={organizationId} showTotemColumn />;
};
